import FormatPrice from "../format-price";
import { taxRateSuffix } from '../../utils/sales-tax/format';
import Currency from "../currency";
import InfoTooltip from "../info-tooltip";
import { sellerEarnings, sellwildFeeTooltipFor } from "../../utils/seller-earnings";
import type { SellerFeeStatus } from "../../schemas/api/seller/fee-status";
import BuyerProtection from "../buyer-protection";
import PoweredByStripe from "../poweredbystripe";
import ConversionButton from "../conversion-button";
import AccountStatus from "../account-status";
import ViewDealDetails from "./ViewDealDetails";
import ContactSellerDetails from "./ContactSellerDetails";
import AdoptionDetails from "./AdoptionDetails";
import shared from "../../data/js/shared";
import { computeCheckoutCharge, toCents } from "../../utils/checkout/create-payment-intent-decisions";
import { isVehicle } from "../utils";
import { isAdoptionListing } from "../../data/js/data-sources";
import { LISTED } from "../../data/js/listing-statuses";
import {
  calculatePartnerMonthlyPayment,
  PARTNER_FINANCING_TERM_MONTHS,
  PARTNER_FINANCING_DOWN_PAYMENT_PERCENT,
} from "../../utils/finance";

const {
  stripeFeeLabel,
  stripeFeeTooltip,
  salesTaxEstimateLabel,
  subtotal,
  totalDue,
  orderDetailsHeading,
  adoptionDetailsHeading,
  yourListingHeading,
  adoptionBuyerProtectionText,
  partnerBuyerProtectionText,
  shippingCalculating,
  totalPending,
  ownerShippingSampleNote,
} = shared.product;

const { manageListingLabel } = shared.ownerMenu;
const { youEarn } = shared.post;

/**
 * The buy box's Total: item, shipping, fee and tax, added in whole cents.
 *
 * It used to add `dec2()` of each, and dec2 truncates through a float: $4.35
 * of shipping is 434.99999… cents and came out $4.34, so the Total sat a cent
 * under the Shipping row above it and under what checkout charges.
 */
export function buyBoxTotal(...amounts: Array<number | string | null | undefined>): number {
  return amounts.reduce<number>((cents, amount) => cents + toCents(Number(amount) || 0), 0) / 100;
}

/**
 * The buy box's Processing line: the fee on the item plus the shipping it is
 * quoted with, through the function the charge uses (computeCheckoutCharge).
 * Stripe is billed that fee ROUNDED to the cent; it used to be truncated here
 * with dec2, so the buy box could print a cent under checkout and the card.
 */
export function buyBoxStripeFee(itemPrice: number | string | null | undefined, shipping: number | string | null | undefined): number {
  return computeCheckoutCharge(toCents(Number(itemPrice) || 0), toCents(Number(shipping) || 0)).stripeFeeCents / 100;
}

const {
  BARGAINHUNTER_DATASOURCE,
  MYEV_DATASOURCE,
  GCC_DATASOURCE,
  LOTLINX_DATASOURCE,
  AMAZON_DATASOURCE,
  SPORTSERVER_DATASOURCE,
  partnerNames,
} = shared.product;

const VIEW_DEAL_PARTNER_DATASOURCES = new Set<number>([
  GCC_DATASOURCE,
  LOTLINX_DATASOURCE,
  AMAZON_DATASOURCE,
  BARGAINHUNTER_DATASOURCE,
  SPORTSERVER_DATASOURCE,
]);

const FINANCED_PARTNER_DATASOURCES = new Set<number>([
  GCC_DATASOURCE,
  LOTLINX_DATASOURCE,
]);

const FINANCING_DOWN_PERCENT = Math.round(PARTNER_FINANCING_DOWN_PAYMENT_PERCENT * 100);

/**
 * One line of the buy box's price breakdown.
 *
 * Every row goes through this so rows cannot drift apart between listing types.
 * They had: the discounted breakdown rendered 35px rows, the standard one 50px,
 * because the two were separate copies of the same markup.
 *
 * The height lives in `.price-row` (styles/pages/product.scss) so there is one
 * number, not one per branch.
 */
const PriceRow = ({
  label,
  aside,
  children,
  bold = false,
  className = '',
}: {
  label: React.ReactNode;
  /** Sits between the label and the value, centred in what is left of the row. */
  aside?: React.ReactNode;
  children: React.ReactNode;
  bold?: boolean;
  className?: string;
}) => (
  <div
    className={`price-row px-5 flex w-full justify-between items-center${bold ? ' font-bold' : ''}${className ? ` ${className}` : ''}`}
  >
    <span>{label}</span>
    {aside ? <span className="price-row__aside">{aside}</span> : null}
    <span className="right">{children}</span>
  </div>
);

/**
 * A value in a price row that is not a rendered `.dollarsCents` — the struck
 * original, the percentage off, the stand-in for a quote still in flight.
 *
 * All three occupy a price's line box, so a row holds its shape whatever is in
 * it. Original and Discount also take a price's type size, because they are
 * what says the listing is a deal and they were the smallest thing in the box.
 * The pending line keeps the box and stays at the row's own size — see
 * `.price-row__value--pending`.
 */
const PriceValue = ({ className, children }: { className: string; children: React.ReactNode }) => (
  <span className={`price-row__value ${className}`}>{children}</span>
);

const PendingPrice = () => (
  <PriceValue className="price-row__value--pending">{shippingCalculating}</PriceValue>
);

/**
 * The Total while the shipping row is still a guess.
 *
 * It takes a price's line box like every other value in the column, so the row
 * does not resize when the real number lands.
 */
const PendingTotal = () => (
  <PriceValue className="price-row__value--dash">{totalPending}</PriceValue>
);

/** The card fee's label, with the rate behind an (i) like checkout's. */
const StripeFeeLabel = () => (
  <span className="inline-flex items-center gap-1.5">
    {stripeFeeLabel}
    <InfoTooltip text={stripeFeeTooltip} />
  </span>
);

/**
 * The Shipping label. On a seller's own listing the quote is to where the seller
 * is browsing from, so an (i) says so rather than it reading as what every buyer
 * pays. It was a line under the row, which pushed the rest of the panel around.
 */
const ShippingLabel = ({ ownerNote, testId }: { ownerNote: boolean; testId: string }) =>
  ownerNote ? (
    <span className="inline-flex items-center gap-1.5">
      Shipping
      <InfoTooltip text={ownerShippingSampleNote} wrap testId={testId} />
    </span>
  ) : (
    <>Shipping</>
  );

/**
 * The owner's You Earn label, with the Sellwild fee explained behind an (i) in
 * the sell page's own words, at this seller's rate.
 */
const YouEarnLabel = ({ feeStatus }: { feeStatus: SellerFeeStatus | null | undefined }) => (
  <span className="inline-flex items-center gap-1.5">
    {youEarn}
    <InfoTooltip text={sellwildFeeTooltipFor(feeStatus)} />
  </span>
);

interface ProductDetailPricingProps {
  product: Record<string, unknown>;
  isOwnListing: boolean | "" | undefined;
  isAvailable: boolean;
  isPendingOrSold: boolean;
  isBuyable: boolean;
  isNonShippable: boolean;
  isGCCorMyEV: boolean;
  isPartnerListing: boolean;
  dataSourceId: unknown;
  remote_url: unknown;
  priceInfo: React.ReactNode;
  currentPrice: number;
  stripeFee: number;
  slashedPrice: unknown;
  percentOff: unknown;
  shippingEstimate: number;
  shippingCarrier: string;
  loadingShipping: boolean;
  /** True when CJ partner-freight returned 0 options — supplier can't fulfill. */
  isOutOfStock?: boolean;
  /** Cross-border route: shipping is arranged between the parties, like a vehicle. */
  shippingArrangedSeparately?: boolean;
  /**
   * Dollars of sales tax the shipping estimate quoted for the visitor's
   * location. 0 for the sellers who do not collect, and then no row is drawn.
   */
  taxEstimate?: number;
  /** The receipt label for that tax ("Sales tax", "HST"); falls back to the shared label. */
  taxLabel?: string;
  /** Effective rate for that tax, shown beside the amount when known. */
  taxRatePercent?: number;
  /** Whether that rate is a state average rather than the buyer's own. */
  taxApproximate?: boolean;
  renderCarrierLogo: (carrier: string) => React.ReactNode;
  // ConversionButton props
  state: Record<string, unknown>;
  requesting: boolean;
  setChatOpen: (open: boolean) => void;
  clickEmailButton: () => void;
  contactShelterButton: () => void;
  openRemoteUrlButton: () => void;
  checkoutUrl: string | undefined;
  preloadSimilarListings: () => void;
  productDataSourceId: number;
  /** Opens the owner menu. Same menu the header ellipsis opens. */
  onManageClick: () => void;
  /** The owner menu anchors itself to whichever control opened it. */
  manageButtonRef: React.Ref<HTMLButtonElement>;
  /** The owner's own Sellwild fee rate, for You Earn. Null while loading: the base rate stands in. */
  ownerFeeStatus?: SellerFeeStatus | null;
}

export const MobilePriceSummary = ({
  isBuyable,
  isOwnListing,
  isNonShippable,
  dataSourceId,
  priceInfo,
  currentPrice,
  stripeFee,
  shippingEstimate,
  shippingCarrier,
  loadingShipping,
  renderCarrierLogo,
  product,
  isOverPriceThreshold,
  isOutOfStock = false,
  shippingArrangedSeparately = false,
  taxEstimate = 0,
  taxLabel,
  taxRatePercent,
  taxApproximate,
  slashedPrice,
  percentOff,
  ownerFeeStatus,
}: {
  isBuyable: boolean;
  isOwnListing: boolean | "" | undefined;
  isNonShippable: boolean;
  dataSourceId: unknown;
  priceInfo: React.ReactNode;
  currentPrice: number;
  stripeFee: number;
  shippingEstimate: number;
  shippingCarrier: string;
  loadingShipping: boolean;
  renderCarrierLogo: (carrier: string) => React.ReactNode;
  product: Record<string, unknown>;
  isOverPriceThreshold: boolean;
  isOutOfStock?: boolean;
  shippingArrangedSeparately?: boolean;
  taxEstimate?: number;
  taxLabel?: string;
  /** Effective rate for that tax, shown beside the amount when known. */
  taxRatePercent?: number;
  /** Whether that rate is a state average rather than the buyer's own. */
  taxApproximate?: boolean;
  /** The owner's own Sellwild fee rate, for You Earn. */
  ownerFeeStatus?: SellerFeeStatus | null;
  /**
   * The price before the listing's discount ("$20"), when it has one. The
   * desktop buy box draws it as Original / Discount / Sale Price; this is the
   * only price block a phone sees, so it draws the same rows.
   */
  slashedPrice?: React.ReactNode;
  percentOff?: number | null;
}) => {
  // Vehicles and cross-border orders read the same way: no rate, no charge.
  const arranged = isVehicle(product) || shippingArrangedSeparately;
  const shippingDisplay = isOutOfStock
    ? 'Out of stock'
    : loadingShipping
      ? shippingCalculating
      : shippingEstimate === 0
        ? 'Free'
        : Currency(shippingEstimate, FormatPrice);
  // A vehicle's tax is settled at registration, never here.
  const taxLine = !arranged && taxEstimate > 0 ? Math.round(taxEstimate * 100) / 100 : 0;
  return (
    <>
      {isBuyable && (
        <div className="mobile-price-summary">
          <div className="order-detail-heading same-color-border">
            {/* Only drawn when buyable, and an adoption (RescueGroups) listing
                is a partner listing, which never is. */}
            {isOwnListing ? yourListingHeading : orderDetailsHeading}
          </div>
          <div className="px-5 py-3">
            {slashedPrice ? (
              <>
                <div className="h-[35px] flex w-full justify-between items-center" data-testid="mobile-original-row">
                  <span>Original</span>
                  <span className="line-through text-gray-400">{slashedPrice}</span>
                </div>
                {percentOff ? (
                  <div className="h-[35px] flex w-full justify-between items-center" data-testid="mobile-discount-row">
                    <span>Discount</span>
                    <span className="font-bold text-[#ff6b35]">{percentOff}% off</span>
                  </div>
                ) : null}
                <div className="h-[35px] flex w-full justify-between items-center font-bold">
                  <span>Sale Price</span>
                  <span>{priceInfo}</span>
                </div>
              </>
            ) : (
              <div className="h-[35px] flex w-full justify-between items-center">
                <span>Subtotal</span>
                <span>{priceInfo}</span>
              </div>
            )}
            {!isNonShippable && (
              <div className="h-[35px] flex w-full justify-between items-center">
                <span>
                  <ShippingLabel
                    ownerNote={Boolean(isOwnListing) && !arranged && !isOutOfStock}
                    testId="mobile-owner-shipping-note"
                  />
                </span>
                {arranged ? (
                  <span>Arranged Separately</span>
                ) : (
                  <>
                    <span>
                      {!loadingShipping && renderCarrierLogo(shippingCarrier)}
                    </span>
                    <span>{shippingDisplay}</span>
                  </>
                )}
              </div>
            )}
            <div className="h-[35px] flex w-full justify-between items-center">
              <StripeFeeLabel />
              <span>{Currency(stripeFee, FormatPrice)}</span>
            </div>
            {taxLine > 0 && !loadingShipping && (
              <div className="h-[35px] flex w-full justify-between items-center" data-testid="mobile-tax-row">
                {/* The row is a fixed 35px, so the label and its rate must not
                    wrap; the rate is muted so the amount stays the thing read. */}
                <span className="whitespace-nowrap">
                  {taxLabel || salesTaxEstimateLabel}
                  {taxRateSuffix(taxRatePercent, taxApproximate) && (
                    <span className="ml-1 text-[#999]" data-testid="mobile-tax-rate">
                      {taxRateSuffix(taxRatePercent, taxApproximate)}
                    </span>
                  )}
                </span>
                <span>{Currency(taxLine, FormatPrice)}</span>
              </div>
            )}
            <div className="h-[35px] flex w-full justify-between items-center border-t border-[#555] mt-2 pt-2 font-bold text-lg">
              <span>{totalDue}</span>
              <span>
                {loadingShipping && !isNonShippable && !arranged
                  ? totalPending
                  : Currency(buyBoxTotal(currentPrice, isNonShippable || arranged ? 0 : shippingEstimate, stripeFee, taxLine), FormatPrice)}
              </span>
            </div>
            {isOwnListing && (
              <div className="h-[35px] flex w-full justify-between items-center" data-testid="mobile-you-earn-row">
                <YouEarnLabel feeStatus={ownerFeeStatus} />
                <span>{Currency(sellerEarnings(currentPrice, ownerFeeStatus), FormatPrice)}</span>
              </div>
            )}
          </div>
        </div>
      )}
      {isBuyable && !isOverPriceThreshold ? <BuyerProtection /> : <div className="no-buyer-protection" />}
    </>
  );
};

export const DesktopOrderInfo = ({
  product,
  isOwnListing,
  isAvailable,
  isPendingOrSold,
  isBuyable,
  isNonShippable,
  dataSourceId,
  // Carried on the props for the conversion button's benefit; not read here.
  remote_url: _remote_url,
  priceInfo,
  currentPrice,
  stripeFee,
  slashedPrice,
  percentOff,
  shippingEstimate,
  shippingCarrier,
  loadingShipping,
  isOutOfStock = false,
  shippingArrangedSeparately = false,
  taxEstimate = 0,
  taxLabel,
  taxRatePercent,
  taxApproximate,
  renderCarrierLogo,
  state: _state,
  requesting,
  setChatOpen,
  clickEmailButton,
  contactShelterButton,
  openRemoteUrlButton,
  checkoutUrl,
  preloadSimilarListings,
  productDataSourceId,
  onManageClick,
  manageButtonRef,
  ownerFeeStatus,
}: ProductDetailPricingProps) => {
  // Vehicles and cross-border orders read the same way: no rate, no charge.
  const arranged = isVehicle(product) || shippingArrangedSeparately;
  const shippingDisplay = isOutOfStock
    ? 'Out of stock'
    : loadingShipping
      ? <PendingPrice />
      : shippingEstimate === 0
        ? 'Free'
        : Currency(shippingEstimate, FormatPrice);
  // A vehicle's tax is settled at registration, never here.
  const taxLine = !arranged && taxEstimate > 0 ? Math.round(taxEstimate * 100) / 100 : 0;
  const totalWithTax = buyBoxTotal(currentPrice, isNonShippable || arranged ? 0 : shippingEstimate, stripeFee, taxLine);
  return (
    <div className="order-info same-color-2 same-color-border" onMouseEnter={preloadSimilarListings} data-screenshot="buy-box">
      <div className={`order-info-inner${isOwnListing ? ' order-info-inner--owner' : ''}`}>
        <div className="order-info-pricing">
          {(isAvailable || isPendingOrSold) && (
            <div className="order-detail-heading same-color-border">
              {isOwnListing ? yourListingHeading : (isAdoptionListing(dataSourceId as number) ? adoptionDetailsHeading : orderDetailsHeading)}
            </div>
          )}
          {(isAvailable || isPendingOrSold) && !isAdoptionListing(dataSourceId as number) && (
            <>
              {/* Desktop breakdown.
                  One list of rows for every kind of listing. This used to fork
                  in two, and the halves had drifted: the discounted branch
                  rendered Original / Discount / Sale Price and then STOPPED, so
                  a buyer on a discounted listing never saw shipping, the
                  processing fee or the total before pressing Buy Now. The
                  discount lines are a prefix to the breakdown, not a
                  replacement for it. */}
              <div
                className={`desktop-price-breakdown text-base ${productDataSourceId === BARGAINHUNTER_DATASOURCE ? 'discount-section-bargainhunter' : ''}`}
              >
                {slashedPrice ? (
                  <>
                    <PriceRow label="Original">
                      <PriceValue className="line-through text-gray-400">{slashedPrice as React.ReactNode}</PriceValue>
                    </PriceRow>
                    {percentOff ? (
                      <PriceRow label="Discount">
                        <PriceValue className="font-bold text-[#ff6b35]">{percentOff as React.ReactNode}% off</PriceValue>
                      </PriceRow>
                    ) : null}
                    <PriceRow label="Sale Price" bold>{priceInfo}</PriceRow>
                  </>
                ) : (
                  <PriceRow label={subtotal}>{priceInfo}</PriceRow>
                )}

                {FINANCED_PARTNER_DATASOURCES.has(productDataSourceId) && currentPrice > 0 && (
                  <div className="px-5 pb-3 -mt-2 flex w-full justify-between items-center text-sm">
                    <InfoTooltip
                      text={`Based on ${FINANCING_DOWN_PERCENT}% down over ${PARTNER_FINANCING_TERM_MONTHS} months`}
                    />
                    <span className="opacity-80">
                      ~{Currency(calculatePartnerMonthlyPayment(currentPrice), FormatPrice)}/mo
                    </span>
                  </div>
                )}

                {isBuyable && !isNonShippable && (
                  // The carrier rides in the row's middle slot rather than beside
                  // the price. Tucked against the number it read as a prefix to
                  // it — a mark on the amount rather than the answer to "who is
                  // carrying this", which is the question it is there to answer.
                  <PriceRow
                    label={
                      <ShippingLabel
                        ownerNote={Boolean(isOwnListing) && !arranged && !isOutOfStock}
                        testId="desktop-owner-shipping-note"
                      />
                    }
                    aside={
                      !arranged && !loadingShipping
                        ? renderCarrierLogo(shippingCarrier)
                        : null
                    }
                  >
                    {arranged ? 'Arranged Separately' : shippingDisplay}
                  </PriceRow>
                )}
                {isBuyable && (
                  <PriceRow label={<StripeFeeLabel />}>{Currency(stripeFee, FormatPrice)}</PriceRow>
                )}
                {isBuyable && taxLine > 0 && !loadingShipping && (
                  <PriceRow
                    label={
                      <span className="whitespace-nowrap">
                        {taxLabel || salesTaxEstimateLabel}
                        {taxRateSuffix(taxRatePercent, taxApproximate) && (
                          <span className="ml-1 text-[#999]" data-testid="desktop-tax-rate">
                            {taxRateSuffix(taxRatePercent, taxApproximate)}
                          </span>
                        )}
                      </span>
                    }
                    className="price-row--tax"
                  >
                    <span data-testid="desktop-tax-amount">{Currency(taxLine, FormatPrice)}</span>
                  </PriceRow>
                )}
                {isBuyable && (
                  <PriceRow label={totalDue} bold className="price-row--total">
                    {loadingShipping && !isNonShippable && !arranged
                      ? <PendingTotal />
                      : Currency(totalWithTax, FormatPrice)}
                  </PriceRow>
                )}
                {isBuyable && isOwnListing && (
                  // What the seller keeps: the price less the Sellwild fee, as
                  // the sell page quotes it. Buyer-paid shipping, the card fee
                  // and tax are not theirs, so they are not in it.
                  <PriceRow label={<YouEarnLabel feeStatus={ownerFeeStatus} />} className="price-row--earn">
                    <span data-testid="desktop-you-earn">{Currency(sellerEarnings(currentPrice, ownerFeeStatus), FormatPrice)}</span>
                  </PriceRow>
                )}
              </div>

              {/* Mobile condensed line. Still two shapes, because a discounted
                  listing reads as a strikethrough pair rather than a sum. */}
              {slashedPrice ? (
                <div className="mobile-price-breakdown px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="line-through text-gray-500 opacity-80 whitespace-nowrap">{slashedPrice as React.ReactNode}</span>
                    <span className="font-semibold whitespace-nowrap">{priceInfo}</span>
                  </div>
                </div>
              ) : (
                <div className="mobile-price-breakdown px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center flex-wrap gap-1.5">
                      <span className="font-semibold whitespace-nowrap">{priceInfo}</span>
                      {isBuyable && !isNonShippable && (
                        <>
                          <span className="text-[#999]">+</span>
                          {arranged ? (
                            <span className="whitespace-nowrap">Arranged Separately</span>
                          ) : (
                            <div className="flex items-center gap-1">
                              {!loadingShipping && renderCarrierLogo(shippingCarrier)}
                              <span className="whitespace-nowrap">{shippingDisplay}</span>
                            </div>
                          )}
                        </>
                      )}
                      {isBuyable && (
                        <>
                          <span className="text-[#999]">+</span>
                          <span className="whitespace-nowrap">{Currency(stripeFee, FormatPrice)}</span>
                        </>
                      )}
                      {isBuyable && taxLine > 0 && !loadingShipping && (
                        <>
                          <span className="text-[#999]">+</span>
                          <span className="whitespace-nowrap">{Currency(taxLine, FormatPrice)}</span>
                        </>
                      )}
                    </div>
                    {isBuyable && (
                      <span className="font-bold whitespace-nowrap">
                        = {loadingShipping && !isNonShippable && !arranged
                          ? totalPending
                          : Currency(totalWithTax, FormatPrice)}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {(() => {
          const outOfStockButton = (
            <button
              type="button"
              disabled
              aria-disabled="true"
              className="conversion-button is-disabled bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed w-full py-3 rounded-lg font-semibold"
              data-testid="conversion-button-out-of-stock"
            >
              Out of Stock
            </button>
          );
          const conversionButton = (
            <ConversionButton
              product={product as unknown as React.ComponentProps<typeof ConversionButton>['product']}
              requesting={requesting}
              setChatOpen={setChatOpen}
              clickEmailButton={clickEmailButton}
              contactShelterButton={contactShelterButton}
              openRemoteUrlButton={openRemoteUrlButton}
              checkoutUrl={checkoutUrl}
              {...({} as Record<string, never>)}
            />
          );
          // Owners get the buyer's view of their own listing — the same
          // breakdown, so they see exactly what a buyer is quoted — but not the
          // buyer's button. It used to be rendered greyed out and inert, which
          // spent the most prominent slot in the panel on a control that exists
          // to be unusable; Manage Listing is the only thing an owner can
          // actually do here, so it is the only button.
          if (isOwnListing) {
            const ownStatus = Number((product as Record<string, unknown>)?.status);
            return (
              <div className="conversion-buttons">
                {/* The owner's read-out of where the listing stands: without it
                    a seller who marked their listing sold or delisted it from
                    the ellipsis menu saw the page come back looking exactly as
                    it did before. */}
                {/* Only when the status is worth reading. LISTED is the
                    default every healthy listing sits in, so showing it said
                    nothing and crowded the panel; the states that DO need
                    saying — Sale Pending, Sold, Not Active — still appear,
                    which is the reason this readout was added. */}
                {!Number.isNaN(ownStatus) && ownStatus !== LISTED && (
                  <div className="own-listing-status" data-testid="own-listing-status">
                    <AccountStatus status={ownStatus} />
                  </div>
                )}
                {/* The owner's actual affordance. This used to be a line of
                    prose pointing at the ellipsis menu, which made the reader
                    go find the control instead of handing it to them. */}
                <button
                  type="button"
                  ref={manageButtonRef}
                  className="own-listing-manage"
                  onClick={onManageClick}
                  data-testid="own-listing-manage"
                >
                  {manageListingLabel}
                </button>
                {/* No BuyerProtection card here. It reads "Receive YOUR purchase
                    as described or your money back" — buyer copy, and this is
                    the seller looking at their own listing, so it was both wrong
                    and the reason this variant was the tallest of the six. */}
              </div>
            );
          }
          if (isBuyable && isOutOfStock) {
            return (
              <>
                <div className="conversion-buttons">{outOfStockButton}</div>
                <BuyerProtection />
              </>
            );
          }
          if (isBuyable) {
            return (
              <>
                <div className="conversion-buttons">
                  {conversionButton}
                  <PoweredByStripe />
                </div>
                <BuyerProtection />
              </>
            );
          }
          if (isAdoptionListing(dataSourceId as number)) {
            return (
              <>
                <div className="conversion-buttons">{conversionButton}</div>
                <AdoptionDetails />
                <BuyerProtection text={adoptionBuyerProtectionText} noLink />
              </>
            );
          }
          let variantContent: React.ReactNode = (
            <ContactSellerDetails variant="chat" />
          );
          let isPartnerVariant = false;
          if (productDataSourceId === MYEV_DATASOURCE) {
            variantContent = <ContactSellerDetails variant="email" />;
            isPartnerVariant = true;
          } else if (VIEW_DEAL_PARTNER_DATASOURCES.has(productDataSourceId)) {
            // Every view-deal source is in partnerNames (data/js/shared.ts).
            const partnerName = partnerNames[productDataSourceId];
            variantContent = (
              <ViewDealDetails
                partnerName={partnerName}
                shippingOptional={FINANCED_PARTNER_DATASOURCES.has(productDataSourceId)}
              />
            );
            isPartnerVariant = true;
          }
          return (
            <>
              <div className="conversion-buttons">{conversionButton}</div>
              {variantContent}
              {isPartnerVariant && <BuyerProtection text={partnerBuyerProtectionText} />}
            </>
          );
        })()}
      </div>
    </div>
  );
};
